import React, { useState, useEffect } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import { BN } from "@coral-xyz/anchor";
import {
  TOKEN_2022_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
  getAssociatedTokenAddressSync,
} from "@solana/spl-token";
import { useProgramContext } from "../contexts/ProgramContext";
import { CONFIG, getECPDAAddresses } from "../config";
import {
  getClaimableEpochs,
  getUserStats,
  getUserClaims,
  ClaimableEpoch,
} from "../utils/indexerClient";

interface EpochInfo {
  epochIndex: number;
  epochStart: number;
  epochEnd: number;
  epochEp: string;
  isFinalized: boolean;
  status: "pending" | "active" | "ended";
}

interface UserStakeInfo {
  balance: string;
  totalStaked: string;
  totalClaimed: string;
}

export const UserDashboard: React.FC = () => {
  const { publicKey } = useWallet();
  const { program, ecProgram } = useProgramContext();

  const [currentEpoch, setCurrentEpoch] = useState<EpochInfo | null>(null);
  const [userStake, setUserStake] = useState<UserStakeInfo | null>(null);
  const [claimableEpochs, setClaimableEpochs] = useState<ClaimableEpoch[]>([]);
  const [claimedEpochs, setClaimedEpochs] = useState<number[]>([]);
  const [claiming, setClaiming] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  const fetchAllData = async () => {
    if (!publicKey || !program) return;

    try {
      setLoading(true);
      await Promise.all([
        fetchCurrentEpoch(),
        fetchUserStake(),
        fetchClaimableEpochs(),
        fetchClaimedEpochs(),
      ]);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentEpoch = async () => {
    if (!program) return;

    try {
      const [globalEpochState] = PublicKey.findProgramAddressSync(
        [Buffer.from("global_epoch_state")],
        program.programId
      );

      const state = await program.account.globalEpochState.fetch(
        globalEpochState
      );
      const epochIndex = state.currentEpochIndex.toNumber();

      if (epochIndex > 0) {
        const [epochPda] = PublicKey.findProgramAddressSync(
          [
            Buffer.from("epoch"),
            new BN(epochIndex).toArrayLike(Buffer, "le", 8),
          ],
          program.programId
        );

        const epoch = await program.account.epoch.fetch(epochPda);
        const now = Math.floor(Date.now() / 1000);
        const start = epoch.epochStart.toNumber();
        const end = epoch.epochEnd.toNumber();

        let status: "pending" | "active" | "ended" = "pending";
        if (now >= start && now < end) status = "active";
        else if (now >= end) status = "ended";

        const merkleRoot = Array.from(epoch.rewardsMerkleRoot);
        const isFinalized = merkleRoot.some((byte) => byte !== 0);

        setCurrentEpoch({
          epochIndex,
          epochStart: start,
          epochEnd: end,
          epochEp: epoch.epochEp.toString(),
          isFinalized,
          status,
        });
      }
    } catch (err) {
      console.error("Error fetching current epoch:", err);
    }
  };

  const fetchUserStake = async () => {
    if (!program || !publicKey) return;

    try {
      const [userState] = PublicKey.findProgramAddressSync(
        [
          Buffer.from("user_state"),
          publicKey.toBuffer(),
          CONFIG.STAKE_MINT.toBuffer(),
        ],
        program.programId
      );

      const userStateAccount = await program.account.userState.fetch(userState);

      const stats = await getUserStats(publicKey.toBase58());

      let totalStakedValue = "0";
      if (stats && stats.totalStaked != null) {
        const numValue = Number(stats.totalStaked);
        if (!isNaN(numValue)) {
          totalStakedValue = (numValue / 1e6).toFixed(4);
        }
      }

      setUserStake({
        balance: (userStateAccount.balance.toNumber() / 1e6).toFixed(4),
        totalStaked: totalStakedValue,
        totalClaimed: stats?.totalClaimed || "0",
      });
    } catch (err) {
      console.error("Error fetching user stake:", err);
      setUserStake({
        balance: "0",
        totalStaked: "0",
        totalClaimed: "0",
      });
    }
  };

  const fetchClaimableEpochs = async () => {
    if (!publicKey) return;

    try {
      const epochs = await getClaimableEpochs(publicKey.toBase58());
      // Note: Removed per-epoch RPC calls to check finalization
      // The indexer already has this information, no need to check onchain
      setClaimableEpochs(epochs);
    } catch (err) {
      console.error("Error fetching claimable epochs:", err);
      setClaimableEpochs([]);
    }
  };

  const fetchClaimedEpochs = async () => {
    if (!publicKey) return;

    try {
      const claims = await getUserClaims(publicKey.toBase58());
      setClaimedEpochs(claims.map((c: any) => c.epochIndex));
    } catch (err) {
      console.error("Error fetching claimed epochs:", err);
    }
  };

  const handleClaim = async (epochIndex: number) => {
    if (!publicKey || !program || !ecProgram) {
      setError("Wallet not connected or programs not loaded");
      return;
    }

    setClaiming(epochIndex);
    setError("");
    setSuccess("");

    try {
      // Find the epoch in claimableEpochs to get the reward data
      const epochData = claimableEpochs.find(
        (e) => e.epochIndex === epochIndex
      );

      if (!epochData) {
        throw new Error("No reward data found for this epoch");
      }

      // Convert hex merkle proof to number arrays
      const merkleProof: number[][] = epochData.merkleProof.map(
        (hexProof: string) => {
          const hex = hexProof.replace(/^0x/, "");
          const bytes: number[] = [];
          for (let i = 0; i < hex.length; i += 2) {
            bytes.push(parseInt(hex.substr(i, 2), 16));
          }
          return bytes;
        }
      );

      const ecPdas = getECPDAAddresses(ecProgram.programId);
      const ecMint = ecPdas.getECMint();

      const userEcTokenAccount = getAssociatedTokenAddressSync(
        ecMint,
        publicKey,
        false,
        TOKEN_2022_PROGRAM_ID,
        ASSOCIATED_TOKEN_PROGRAM_ID
      );

      await program.methods
        .claim(
          new BN(epochIndex),
          new BN(epochData.claimableReward),
          merkleProof
        )
        .accounts({
          user: publicKey,
          userEcTokenAccount,
        })
        .rpc({ commitment: "confirmed" });

      setSuccess(
        `Successfully claimed ${epochData.claimableReward} EP for epoch ${epochIndex}!`
      );

      // Wait for the indexer to process the claim event
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Refresh all data
      await fetchAllData();

      // Auto-clear success message and scroll to top
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 500);
    } catch (err: any) {
      console.error("Claim error:", err);

      // Check if the error is about transaction already processed
      const isAlreadyProcessed =
        err.message?.includes("already been processed") ||
        err.message?.includes("already processed") ||
        err.message?.includes("This transaction has already been processed");

      if (isAlreadyProcessed) {
        // Transaction might have succeeded, verify the claim
        console.log("Transaction already processed, verifying claim status...");

        // Wait for indexer to catch up
        await new Promise((resolve) => setTimeout(resolve, 3000));

        try {
          const freshClaims = await getUserClaims(publicKey.toBase58());
          const wasClaimed = freshClaims.some(
            (c: any) => c.epochIndex === epochIndex
          );

          if (wasClaimed) {
            setSuccess(
              `Rewards for epoch ${epochIndex} have been claimed successfully!`
            );
            await fetchAllData();
            setTimeout(() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }, 500);
            return;
          }
        } catch (checkErr) {
          console.error("Error verifying claim status:", checkErr);
        }

        // If we can't verify, refresh and show helpful message
        setError(
          "Transaction may have already been processed. Refreshing your data..."
        );
        await fetchAllData();
        return;
      }

      // Check for AlreadyClaimed program error
      if (
        err.message?.includes("AlreadyClaimed") ||
        err.message?.includes("6006")
      ) {
        setError(`Rewards for epoch ${epochIndex} have already been claimed.`);
        await fetchAllData();
        return;
      }

      // Generic error - show shortened message
      const errorMsg = err.message || "Failed to claim rewards";
      setError(
        errorMsg.length > 150
          ? "Transaction failed. Please try again."
          : errorMsg
      );
    } finally {
      setClaiming(null);
    }
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const getTimeRemaining = () => {
    if (!currentEpoch) return null;

    const now = Math.floor(Date.now() / 1000);
    const { epochStart, epochEnd, status } = currentEpoch;

    if (status === "pending") {
      return { label: "Starts in", seconds: epochStart - now };
    } else if (status === "active") {
      return { label: "Ends in", seconds: epochEnd - now };
    }
    return null;
  };

  useEffect(() => {
    if (publicKey && program) {
      fetchAllData();
      // Poll every 2 minutes instead of 30s to reduce RPC calls
      const interval = setInterval(() => fetchAllData(), 120000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicKey, program]);

  // Also refresh when page becomes visible (user returns to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && publicKey && program) {
        fetchAllData();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicKey, program]);

  const formatTimeRemaining = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    if (mins > 0) return `${mins}m`;
    return "Soon";
  };

  const timeRemaining = getTimeRemaining();

  if (!publicKey) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="card max-w-md text-center">
          <h2 className="text-2xl font-bold text-habitat-primary mb-4">
            Welcome to Habitat Staking
          </h2>
          <p className="text-habitat-neutral-600 mb-6">
            Connect your wallet to view your staking dashboard and start earning
            rewards.
          </p>
          <div className="grid gap-4 text-left">
            <div className="p-4 bg-habitat-neutral-50 rounded-habitat">
              <h3 className="font-semibold text-habitat-primary mb-2">
                Secure Staking
              </h3>
              <p className="text-sm text-habitat-neutral-600">
                Lock your tokens to earn rewards over time
              </p>
            </div>
            <div className="p-4 bg-habitat-neutral-50 rounded-habitat">
              <h3 className="font-semibold text-habitat-primary mb-2">
                Track Rewards
              </h3>
              <p className="text-sm text-habitat-neutral-600">
                View your earnings and claim rewards directly
              </p>
            </div>
            <div className="p-4 bg-habitat-neutral-50 rounded-habitat">
              <h3 className="font-semibold text-habitat-primary mb-2">
                Epoch-Based
              </h3>
              <p className="text-sm text-habitat-neutral-600">
                Rewards distributed at the end of each epoch
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-habitat">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-habitat">
          {success}
        </div>
      )}

      {/* Current Epoch Status */}
      {currentEpoch && (
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-habitat-neutral-900">
              Current Epoch
            </h2>
            <span
              className={`px-4 py-2 rounded-full text-sm font-semibold ${
                currentEpoch.status === "active"
                  ? "bg-green-100 text-green-800"
                  : currentEpoch.status === "pending"
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {currentEpoch.status === "active"
                ? "Active"
                : currentEpoch.status === "pending"
                ? "Pending"
                : "Ended"}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-habitat-neutral-500 mb-1">
                Epoch Number
              </p>
              <p className="text-2xl font-bold text-habitat-primary">
                #{currentEpoch.epochIndex}
              </p>
            </div>
            <div>
              <p className="text-sm text-habitat-neutral-500 mb-1">
                Reward Pool
              </p>
              <p className="text-2xl font-bold text-habitat-secondary">
                {Number(currentEpoch.epochEp).toLocaleString()} EP
              </p>
            </div>
            <div>
              <p className="text-sm text-habitat-neutral-500 mb-1">
                {timeRemaining ? timeRemaining.label : "Status"}
              </p>
              <p className="text-2xl font-bold text-habitat-neutral-900">
                {timeRemaining
                  ? formatTimeRemaining(timeRemaining.seconds)
                  : "Ended"}
              </p>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-habitat-neutral-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-habitat-neutral-500">Start:</span>
                <span className="ml-2 text-habitat-neutral-900">
                  {formatTime(currentEpoch.epochStart)}
                </span>
              </div>
              <div>
                <span className="text-habitat-neutral-500">End:</span>
                <span className="ml-2 text-habitat-neutral-900">
                  {formatTime(currentEpoch.epochEnd)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Stake Info */}
      {userStake && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card">
            <p className="text-sm text-habitat-neutral-500 mb-2">
              Current Stake
            </p>
            <p className="text-3xl font-bold text-habitat-primary">
              {userStake.balance}
            </p>
            <p className="text-sm text-habitat-neutral-400 mt-1">tokens</p>
          </div>
          <div className="card">
            <p className="text-sm text-habitat-neutral-500 mb-2">
              Total Staked
            </p>
            <p className="text-3xl font-bold text-habitat-secondary">
              {userStake.totalStaked}
            </p>
            <p className="text-sm text-habitat-neutral-400 mt-1">lifetime</p>
          </div>
          <div className="card">
            <p className="text-sm text-habitat-neutral-500 mb-2">
              Total Claimed
            </p>
            <p className="text-3xl font-bold text-habitat-accent">
              {userStake.totalClaimed}
            </p>
            <p className="text-sm text-habitat-neutral-400 mt-1">EC</p>
          </div>
        </div>
      )}

      {/* Claimable Rewards */}
      {claimableEpochs.length > 0 && (
        <div className="card">
          <h2 className="text-2xl font-bold text-habitat-neutral-900 mb-6">
            Claimable Rewards
          </h2>
          <div className="space-y-4">
            {claimableEpochs.map((epoch) => (
              <div
                key={epoch.epochIndex}
                className="p-6 bg-habitat-neutral-50 rounded-habitat-lg border border-habitat-neutral-200"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-habitat-neutral-900">
                    Epoch #{epoch.epochIndex}
                  </h3>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                    Ready to Claim
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                  <div>
                    <p className="text-habitat-neutral-500">Claimable Reward</p>
                    <p className="font-semibold text-habitat-primary">
                      {epoch.claimableReward} EP
                    </p>
                  </div>
                  <div>
                    <p className="text-habitat-neutral-500">
                      Your Stake-Seconds
                    </p>
                    <p className="font-semibold">{epoch.userStakeSeconds}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-habitat-neutral-500">Period</p>
                    <p className="font-semibold text-xs">
                      {formatTime(epoch.epochStart)} -{" "}
                      {formatTime(epoch.epochEnd)}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => handleClaim(epoch.epochIndex)}
                  disabled={claiming !== null}
                  className={`w-full md:w-auto btn-primary ${
                    claiming !== null ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {claiming === epoch.epochIndex
                    ? "Claiming..."
                    : "Claim Rewards"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Claimed History */}
      {claimedEpochs.length > 0 && (
        <div className="card">
          <h2 className="text-2xl font-bold text-habitat-neutral-900 mb-4">
            Claimed History
          </h2>
          <p className="text-habitat-neutral-600">
            You have claimed rewards for epochs:{" "}
            {claimedEpochs.sort((a, b) => a - b).join(", ")}
          </p>
        </div>
      )}

      {/* No Rewards Message */}
      {claimableEpochs.length === 0 && !loading && (
        <div className="card text-center">
          <p className="text-habitat-neutral-600">
            No claimable rewards at the moment. Stake tokens to start earning!
          </p>
        </div>
      )}
    </div>
  );
};
